import { Unsubscribe } from 'router5/dist/types/base';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { PagingViewModel } from '../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import TrackingServiceProvider from '../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../tracking-service/tracking-values';
import { IRouter } from '../../infrastructure/router';
import { RouteParams, Routes } from '../../routes-table';
import { IAccountDetailsDTO, IAccountsDAO } from '../../api/accounts.dao';
import { RADIX_PARAM } from '../../constants/common.constants';
import { AccountAdminSettingsType, IAccountAdminDAO, IAccountAdminDTO } from '../../api/account-admin';
import { IAlertsService } from '../../infrastructure/alerts';
import { IErrorParsing } from '../../infrastructure/error-parsing';
import { IntlShape, MessageDescriptor } from 'react-intl';
import { ISubWindowFacade, ISubWindowParameters, ISubWindowsService, SubWindowStatus } from '../../infrastructure/sub-windows';
import { DataAnonymizationFormVM } from './_data-anonymization-form/data-anonymization-form.viewmodel';
import { DataAnonymizationForm } from './_data-anonymization-form/data-anonymization-form';
import { ThirdPartyRegistrationFormVM } from './_third-party-registration/third-party-registration-form.viewmodel';
import { ThirdPartyRegistrationForm } from './_third-party-registration/third-party-registration-form';
import { RecurringZoomMeetingsFormVM } from './_recurring-zoom-meetings/recurring-zoom-meetings-form.viewmodel';
import { RecurringZoomMeetingsForm } from './_recurring-zoom-meetings/recurring-zoom-meetings-form';
import ComplianceEmailsFormVM from './complaince-emails/complaince-emails-form.viewmodel';
import { ComplianceEmailsForm } from './complaince-emails/complaince-emails-form';
import { IReactIntlProvider } from '../../infrastructure/react-intl-provider';
import {
    COMPLIANCE_EMAIL_CAPTURE,
    CONFIGURE_3RD_PARTY_REGISTRATION,
    CONFIGURE_ACCOUNT_SUBDOMAIN,
    CONFIGURE_DATA_ANONYMIZATION,
    CONFIGURE_DATA_SEGREGATION_TEXT,
    CONFIGURE_RECURRING_MEETINGS,
    MANAGE_REGISTRATION_APPROVAL_VIA_API,
    MANAGE_ORDER_BOOKING,
    URL_PARAMETER_REGISTRATION_TEXT,
    CONFIGURE_OE_PASSPORT_ATTENDEE_TEXT,
    CONFIGURE_AI_AGENT_ACCESS_TEXT,
} from './account-admin.text';
import { RegistrationApprovalForm } from './_registration_approval_via_api/registration-approval-form';
import { RegistrationApprovalFormVM } from './_registration_approval_via_api/registration-approval-form.viewmodel';
import { AccountAdminSettingEntity, IAccountAdminSettingsDTO } from './common/account-admin-setting.entity';
import { Mapper } from '../../infrastructure/mapper';
import { SegregatedDataEncryptionForm } from './_segregated-data-encryption/segregated-data-encryption-form';
import { SegregatedDataEncryptionFormVM } from './_segregated-data-encryption/segregated-data-encryption-form.viewmodel';
import { AccountSubdomainFormVM } from './_account-subdomain/account-subdomain-form.viewmodel';
import { AccountSubdomainForm } from './_account-subdomain/account-subdomain-form';
import { OrderBookingFormVM } from './_order-booking/order-booking-form.viewmodel';
import { OrderBookingForm } from './_order-booking/order-booking-form';
import URLParameterRegistrationFormVM from './url-parameter-registration/url-parameter-registration-form.viewmodel';
import { URLParameterRegistrationForm } from './url-parameter-registration/url-parameter-registration-form';
import PassportAttendeeSSOFormVM from './_oe-passport-attendee-sso/oe-passport-attendee-sso-form.viewmodel';
import { OEPassportAttendeeSSOForm } from './_oe-passport-attendee-sso/oe-passport-attendee-sso-form';
import { AIAgentAccessFormVM } from './_ai-agent-access/ai-agent-access-form.viewmodel';
import { AIAgentAccessForm } from './_ai-agent-access/ai-agent-access-form';
import { hasPermission } from '../../infrastructure/utils/has-permissions';
import { Permission } from '../../api/roles.dao';


interface ISubwindowsMap {
    type: AccountAdminSettingsType;
    popup: ISubWindowFacade;
};

type TAccoundAdminSettingsVM = DataAnonymizationFormVM
    | ThirdPartyRegistrationFormVM
    | AccountSubdomainFormVM
    | RegistrationApprovalFormVM
    | RecurringZoomMeetingsFormVM
    | ComplianceEmailsFormVM
    | SegregatedDataEncryptionFormVM
    | OrderBookingFormVM
    | URLParameterRegistrationFormVM
    | PassportAttendeeSSOFormVM
    | AIAgentAccessFormVM;

export enum AccountAdminTab {
    Settings = 'Settings',
    EventAttributes = 'EventAttributes'
}

class AccountAdminVM implements IModuleVM {
    @mobx.observable
    public isLoading: boolean;
    private subscriptions: Unsubscribe[];
    @mobx.observable
    public paginationVM: PagingViewModel;
    @mobx.observable
    private account: IAccountDetailsDTO;
    @mobx.observable
    private formPopupMaps: ISubwindowsMap[] = [];
    @mobx.observable
    private entities: AccountAdminSettingEntity[] = [];
    @mobx.observable
    private activeTab: AccountAdminTab = AccountAdminTab.Settings;
    private mapper: Mapper = new Mapper();

    constructor(
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly accountsDAO: IAccountsDAO = IoC.Container.get(IAccountsDAO),
        @IoC.Inject private readonly accountAdminDAO: IAccountAdminDAO = IoC.Container.get(IAccountAdminDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    ) {
        this.mapAccountAdminDTOToAccountAdminSettingEntityDTO();
    }

    public get intl(): IntlShape { return this.intlProvider.getIntl(); }
    public get entitiesArray(): AccountAdminSettingEntity[] { return this.entities; }

    didMount(): void {
        this.subscriptions = [];

        const currentRoute = this.router.router.getState();
        const currentRouteName = currentRoute.name;
        const accountId = currentRoute.params.accountId;

        if (currentRouteName === 'AccountAdmin') {
            this.router.navigate(Routes.AccountAdminSettings, { accountId: accountId });
            mobx.runInAction(() => {
                this.activeTab = AccountAdminTab.Settings;
            });
        } else if (currentRouteName === 'AccountAdminEventAttributes') {
            mobx.runInAction(() => {
                this.activeTab = AccountAdminTab.EventAttributes;
            });
        } else {
            mobx.runInAction(() => {
                this.activeTab = AccountAdminTab.Settings;
            });
        }

        this.subscriptions.push(
            this.router.listen(Routes.AccountAdmin, (params: { accountId: string }) => {
                this.router.navigate(Routes.AccountAdminSettings, { accountId: params.accountId });
                this.handleRouteChange(AccountAdminTab.Settings, params);
            })
        );

        this.subscriptions.push(
            this.router.listen(Routes.AccountAdminSettings, (params: { accountId: string }) => {
                this.handleRouteChange(AccountAdminTab.Settings, params);
            })
        );

        this.subscriptions.push(
            this.router.listen(Routes.AccountAdminEventAttributes, (params: { accountId: string }) => {
                if (hasPermission(Permission.VIEW_EVENT_ATTRIBUTES)) {
                    this.handleRouteChange(AccountAdminTab.EventAttributes, params);
                } else {
                    this.router.navigate(Routes.AccountAdminSettings, { accountId: params.accountId });
                }
            })
        );

        this.trackAccountAdminPageLoadMixPanel();
    }

    private handleRouteChange = (tab: AccountAdminTab, params: { accountId: string }): void => {
        mobx.runInAction(() => {
            this.activeTab = tab;
            this.isLoading = true;
            this.fetchAccount(params.accountId).then(() => {
                this.fetchAccountAdmin();
            });
        });
    };

    willUnmount(): void {
        this.subscriptions.forEach(x => x());
    }

    @mobx.action
    public fetchAccountAdmin = async (): Promise<void> => {
        try {
            const params = {
                accountId: this.account.accountId,
            };

            const response = await this.accountAdminDAO.getAccountAdmin(params);
            this.onFetchAccountAdminSuccess(response);
        } catch (error) {
            this.onFetchAccountAdminError(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    private onFetchAccountAdminSuccess = (response: IAccountAdminDTO): void => {
        const mappedResponse = response.map(setting => this.mapper.map(setting, 'IAccountAdminSettingsDTO'));
        this.entities = mappedResponse.map((settings: IAccountAdminSettingsDTO) => new AccountAdminSettingEntity(settings));
    };

    @mobx.action
    private onFetchAccountAdminError = (error): void => {
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error)
        });
    };

    @mobx.action
    private fetchAccount = async (accountIdString: string): Promise<void> => {
        const accountId = parseInt(accountIdString, RADIX_PARAM);

        if (!this.account || accountId !== this.account.accountId) {
            let account: IAccountDetailsDTO;

            try {
                account = await this.accountsDAO.readAccount(accountId);
            } catch (error) {
                this.isLoading = false;
                this.router.navigate(Routes.AccountsList, {});
            }

            if (account) this.setAccount(account);
        }
    };


    public getEntityByType = (type: AccountAdminSettingsType): AccountAdminSettingEntity => {
        return this.entities.find(x => x.type === type);
    };

    private get routeParams(): { accountId: string } {
        return this.router.router.getState().params;
    }

    public getAccountId(): number {
        return parseInt(this.routeParams.accountId, RADIX_PARAM);
    }

    public getActiveTab(): AccountAdminTab {
        return this.activeTab;
    }

    @mobx.action
    public setActiveTab = (tab: AccountAdminTab): void => {
        this.activeTab = tab;
    };

    public showAccountSettingForm = (type: AccountAdminSettingsType): void => {
        const entity = this.getEntityByType(type);
        let subWindowObject: ISubWindowParameters<unknown> = {} as ISubWindowParameters<unknown>;
        let vm: TAccoundAdminSettingsVM;

        switch (type) {
            case AccountAdminSettingsType.DATA_ANONYMIZATION:
                vm = new DataAnonymizationFormVM(entity);
                subWindowObject = this.composeSubWindowObject(
                    DataAnonymizationForm as React.ComponentType<unknown>, vm, CONFIGURE_DATA_ANONYMIZATION
                );
                break;
            case AccountAdminSettingsType.THIRD_PARTY_REGISTRATION:
                vm = new ThirdPartyRegistrationFormVM(entity);
                subWindowObject = this.composeSubWindowObject(
                    ThirdPartyRegistrationForm as React.ComponentType<unknown>, vm, CONFIGURE_3RD_PARTY_REGISTRATION
                );
                break;
            case AccountAdminSettingsType.ACCOUNT_SUBDOMAIN:
                vm = new AccountSubdomainFormVM(entity);
                subWindowObject = this.composeSubWindowObject(
                    AccountSubdomainForm as React.ComponentType<unknown>, vm, CONFIGURE_ACCOUNT_SUBDOMAIN
                );
                break;
            case AccountAdminSettingsType.MANAGE_REGISTRATION_APPROVAL:
                vm = new RegistrationApprovalFormVM(entity);
                subWindowObject = this.composeSubWindowObject(
                    RegistrationApprovalForm as React.ComponentType<unknown>, vm, MANAGE_REGISTRATION_APPROVAL_VIA_API
                );
                break;
            case AccountAdminSettingsType.MANAGE_ORDER_BOOKING:
                vm = new OrderBookingFormVM(entity);
                subWindowObject = this.composeSubWindowObject(
                    OrderBookingForm as React.ComponentType<unknown>, vm, MANAGE_ORDER_BOOKING
                );
                break;
            case AccountAdminSettingsType.RECURRING_ZOOM_MEETINGS:
                vm = new RecurringZoomMeetingsFormVM(entity);
                subWindowObject = this.composeSubWindowObject(
                    RecurringZoomMeetingsForm as React.ComponentType<unknown>, vm, CONFIGURE_RECURRING_MEETINGS
                );
                break;
            case AccountAdminSettingsType.COMPLIANCE_EMAIL_CAPTURE:
                vm = new ComplianceEmailsFormVM(entity);
                subWindowObject = this.composeSubWindowObject(
                    ComplianceEmailsForm as React.ComponentType<unknown>, vm, COMPLIANCE_EMAIL_CAPTURE
                );
                break;
            case AccountAdminSettingsType.SEGREGATED_DATA_ENCRYPTION:
                vm = new SegregatedDataEncryptionFormVM(entity);
                subWindowObject = this.composeSubWindowObject(
                    SegregatedDataEncryptionForm as React.ComponentType<unknown>, vm, CONFIGURE_DATA_SEGREGATION_TEXT
                );
                break;
            case AccountAdminSettingsType.URL_PARAMETER_REGISTRATION:
                vm = new URLParameterRegistrationFormVM(entity);
                subWindowObject = this.composeSubWindowObject(
                    URLParameterRegistrationForm as React.ComponentType<unknown>, vm, URL_PARAMETER_REGISTRATION_TEXT
                );
                break;
            case AccountAdminSettingsType.OE_PASSPORT_ATTENDEE_SSO:
                vm = new PassportAttendeeSSOFormVM(entity);
                subWindowObject = this.composeSubWindowObject(
                    OEPassportAttendeeSSOForm as React.ComponentType<unknown>, vm, CONFIGURE_OE_PASSPORT_ATTENDEE_TEXT
                );
                break;
            case AccountAdminSettingsType.AI_AGENT_ACCESS:
                vm = new AIAgentAccessFormVM(entity, this.account?.name);
                subWindowObject = this.composeSubWindowObject(
                    AIAgentAccessForm as React.ComponentType<unknown>, vm, CONFIGURE_AI_AGENT_ACCESS_TEXT
                );
                break;
        }

        subWindowObject = {
            ...subWindowObject,
            onCloseIconClickHandler: (): boolean => {
                entity.reset();

                return true;
            }
        };

        this.showForm(type, subWindowObject);
    };

    private showForm = (type: AccountAdminSettingsType, subWindowObject: ISubWindowParameters<unknown>): void => {
        let formPopup = this.getFormPopupByType(type)?.popup;
        if (!formPopup || formPopup?.getStatus() === SubWindowStatus.Closed) {
            subWindowObject.onCloseIconClickHandler = (): void => {
                formPopup.close();
                this.getEntityByType(type).reset();
            };
            formPopup = this.subWindowService.showSubWindow(subWindowObject);
            this.setFormPopupByType(type, formPopup);
        } else {
            formPopup.maximize();
        }
    };

    private setFormPopupByType = (type: AccountAdminSettingsType, popup: ISubWindowFacade): void => {
        const formPopup = this.getFormPopupByType(type);
        if (!formPopup) {
            this.formPopupMaps.push({ type: type, popup: popup });
        } else {
            formPopup.popup = popup;
        }
    };

    @mobx.action
    private setAccount = (account: IAccountDetailsDTO): void => {
        this.account = account;
    };

    private trackAccountAdminPageLoadMixPanel = (): void => {
        this.trackingService.get().track(TRACKING_EVENTS.ACCOUNT_ADMIN_PAGE_LOAD, {});
    };

    private getFormPopupByType = (type: AccountAdminSettingsType): ISubwindowsMap => {
        return this.formPopupMaps.find(x => x.type === type);
    };

    private composeSubWindowObject = (
        component: React.ComponentType<unknown>, vm: TAccoundAdminSettingsVM, title: MessageDescriptor
    ): ISubWindowParameters<unknown> => {
        return {
            componentToHost: component,
            componentToHostProps: {
                vm: vm,
            },
            title: this.intl.formatMessage(title),
        };
    };

    /**
     * Maps the properties of an IAccountAdminSettingsDTO object to an AccountAdminSettingEntityDTO.
     * Utilizes the mapper to define the mapping between the DTO fields and the entity fields.
     * This method configures the mapping for various account admin settings
     */
    private mapAccountAdminDTOToAccountAdminSettingEntityDTO = (): void => {
        this.mapper.addMapping(
            'IAccountAdminSettingsDTO',
            {
                type: 'type',
                accountId: 'accountId',
                anonymizationEnabled: 'enabled',
                thirdPartyRegistrationEnabled: 'enabled',
                recurringZoomMeetingsEnabled: 'enabled',
                complianceEmailCaptureEnabled: 'enabled',
                registrationApprovalEnabled: 'enabled',
                orderBookingEnabled: 'enabled',
                segregatedDataEncryptionEnabled: 'enabled',
                urlParameterRegistrationEnabled: 'enabled',
                passportAttendeeSSOEnabled: 'enabled',
                aiAgentAccessEnabled: 'enabled',
                daysCountBeforeAnonymization: 'additionalData',
                allowedZoomGroups: 'additionalData',
                complianceEmails: 'additionalData',
                internalNotificationEmails: 'additionalData',
                accountSubdomainEnabled: 'enabled',
                subdomain: 'additionalData',
                authenticationMethod: 'additionalData',
                coordinatorDeniedNotificationsEnabled: 'coordinatorDeniedNotificationsEnabled',
                requestAccessCodeRegistrationApprovalEnabled: 'requestAccessCodeRegistrationApprovalEnabled',
            }
        );
    };
}

export { AccountAdminVM };
